/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useEffect } from 'react';
import { jsx } from "@emotion/core";
import { setSetting } from '../../Components/action/common';
import { pushToSetting } from '../../redux/slices/SettingSclice';
import { useDispatch } from 'react-redux';
import { Cancel, KeyboardIcon, ResetIcon } from '../../Components/LandingHeader/Feedback/icons/Icons';
import { getKeyboardSymbol, isMac } from '../../Components/action/utilities';
import Tooltip from '../../Components/common/Tooltip';

const ShortcutKeyCombination = ({name, pressedKeys = [], recordingField = null, onSetDefault = () => {}, onReset = () => {}}) => {
    const isComplete = pressedKeys.length > 0 && !['Control', 'Shift', 'Alt', 'Meta', 'Ctrl', 'Cmd'].includes(pressedKeys[pressedKeys.length - 1])

    return (
        <label className='input-field filed-data-block testing-code-field flex gap-[5px] addTask-field flex items-center justify-center' style={{cursor: 'pointer'}}>
            {
                pressedKeys?.length > 0 
                ? 
                <div className={`shortcut-key-combination flex gap-[10px] items-center justify-center w-full ${recordingField === name ? 'recording-shortcut' : ''}`}>
                    {pressedKeys.map((item, index) => (
                        <React.Fragment key={item}>
                            <div className='shortcut-key-combination-item flex gap-[5px] items-center justify-center'>{item} {getKeyboardSymbol(item)}</div>
                            {(pressedKeys.length > index + 1 || !isComplete) && <span>+</span>}
                        </React.Fragment>
                    ))}
                    <span css={{
                        flex: '1 0 0px',
                        alignSelf: 'stretch'
                    }}></span>
                    <Tooltip className='overDue-tooltip' wrapperClassName="relative" text="Remove shortcut">
                        <a href='#' className='reset-shortcut' onClick={onReset}><Cancel /></a>
                    </Tooltip>
                </div>
                : 
                <>
                    <input type="text" readOnly defaultValue={'Press keys...'} className="text-black bg-white dark:bg-black dark:text-white" />
                    <span css={{
                        flex: '1 0 0px',
                        alignSelf: 'stretch'
                    }}></span>
                    
                    <Tooltip className='overDue-tooltip' wrapperClassName="relative" text="Reset to default">
                        <a href='#' className='reset-shortcut' onClick={onSetDefault}><ResetIcon /></a>
                    </Tooltip>
                </>
            }
        </label>
    )
}

const RecordButton = ({name, recordingField, setRecordingField, settings, handleKeyDown}) => {
    return (
        <button type="button" className='changeName shortcut-button' onClick={() => setRecordingField(name)} onKeyDown={e => handleKeyDown(e, name)}>
            {
                recordingField === name 
                ? 
                <div className='flex gap-[10px] items-center justify-center'>
                    <KeyboardIcon color={`var(--accent-color)`} />
                    <span>Recording...</span>
                </div>
                : 
                (settings?.keyboardShortcuts?.[name]?.length > 0 ? 'Change' : 'Record')
            }
            </button>
    )
}

const KeyboardShortcuts = ({settings, theme}) => {
    const [recordingField, setRecordingField] = useState(null)

    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!recordingField) return;
            
            const activeInput = event.target.closest('.input-field');
            if (!activeInput || !activeInput.classList.contains(`${recordingField}-field`)) {
                setRecordingField(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [recordingField]);

    const defaultShortcuts =   {
        addTask: [isMac ? 'Cmd' : 'Ctrl', 'Enter'],
        searchTask: [isMac ? 'Cmd' : 'Ctrl', 'K'],
        openAssistant: [isMac ? 'Cmd' : 'Ctrl', 'L']
    }
    const [shortcut, setShortcut] = useState({
        addTask: typeof settings?.keyboardShortcuts == 'undefined' ? defaultShortcuts.addTask : settings?.keyboardShortcuts?.addTask,
        searchTask: typeof settings?.keyboardShortcuts == 'undefined' ? defaultShortcuts.searchTask : settings?.keyboardShortcuts?.searchTask,
        openAssistant: typeof settings?.keyboardShortcuts == 'undefined' ? defaultShortcuts.openAssistant : settings?.keyboardShortcuts?.openAssistant
    })

    const dispatch = useDispatch()

    const handleKeyDown = (e, action) => {
        e.preventDefault()
        e.stopPropagation()

        // Handle ESC key to cancel recording
        if (e.key === 'Escape') {
            setRecordingField(null);
            return;
        }

        if(recordingField === null){
            return;
        }

        const keys = [];
        let isComplete = false

        if (e.ctrlKey) keys.push('Ctrl');
        if (e.shiftKey) keys.push('Shift');
        if (e.altKey) keys.push('Alt');
        if (e.metaKey) keys.push('Cmd'); // Cmd on Mac

        const key = e.key;

        // Avoid duplicates and modifier-only keys  
        if (!['Control', 'Shift', 'Alt', 'Meta', 'Ctrl', 'Cmd'].includes(key)) {
            keys.push(key.length === 1 ? key.toUpperCase() : key);
            isComplete = true
        }

        setShortcut(shortcut => {
            const newShortcut = {...shortcut, [action]: keys}
            
            if (isComplete) {
                saveShortcut(newShortcut)
            }
            return newShortcut
        })
    }

    const saveShortcut = async (shortcut) => {
        const newSettings = { ...settings, keyboardShortcuts: {...shortcut} };
        dispatch(pushToSetting(newSettings));
        try {
            await setSetting({ "settings": newSettings })
            setRecordingField(null);
        } catch ({ response }) { }
    }

    const resetShortcut = async (e, action) => {
        e.preventDefault()
        e.stopPropagation()
        const newShortcut = {...(settings?.keyboardShortcuts ?? defaultShortcuts), [action]: []};
        setShortcut(newShortcut)
        const newSettings = { ...settings, keyboardShortcuts: newShortcut };
        dispatch(pushToSetting(newSettings));
        try {
            await setSetting({ "settings": newSettings })
            setRecordingField(null);
        } catch ({ response }) { }
    }

    const setDefaultShortcut = async (e, action) => {
        e.preventDefault()
        e.stopPropagation()
        const newShortcut = {...(settings?.keyboardShortcuts ?? defaultShortcuts), [action]: defaultShortcuts[action]};
        setShortcut(newShortcut)
        const newSettings = { ...settings, keyboardShortcuts: newShortcut };
        dispatch(pushToSetting(newSettings));
        try {
            await setSetting({ "settings": newSettings })
            setRecordingField(null);
        } catch ({ response }) { }
    }

    return (
        <div id='keyboard-shortcuts' css={{
            'button':{
                background: `linear-gradient(0deg, rgba(var(--theme-mode-rgb-white), 0.9), rgba(var(--theme-mode-rgb-white), 0.9)), linear-gradient(0deg, var(--accent-color), var(--accent-color)), linear-gradient(0deg, rgba(var(--theme-mode-rgb-white-reverse), 0.05), rgba(var(--theme-mode-rgb-white-reverse), 0.05)), var(--theme-mode-color-white) !important`,
                color: `var(--accent-color) !important`
            },

            '.shortcut-key-combination':{
                '&.recording-shortcut':{
                    '.shortcut-key-combination-item':{
                        background: `linear-gradient(0deg, var(--accent-color), var(--accent-color)), radial-gradient(99.67% 66.67% at 50% 70.83%, #FFFFFF 0%, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.924496) 100%) !important`
                    }
                }
            }
        }}>
            <div className="field-block mt-[30px]">
                <h3 className="form-heading uppercase mb-0" style={{ color: theme == "dark" ? "white" : "", opacity: theme == "dark" ? 0.7 : '' }}> Keyboard shortcuts </h3>
                <div className="field-info mb-30" style={{color: theme == "dark" ? "white" : "#909090"}}>Click Record button and press a key combination (e.g. Ctrl + S)</div>
            </div>  
            <div className="field-block mt-[30px]">
                <label className="field-label" style={{ color: theme == "dark" ? "white" : "", opacity: theme == "dark" ? 0.7 : '' }}>Add a new task</label>
                <div className="field-data">
                    <ShortcutKeyCombination name='addTask' pressedKeys={shortcut?.addTask} recordingField={recordingField} settings={settings} onReset={(e) => resetShortcut(e, 'addTask')} onSetDefault={(e) => setDefaultShortcut(e, 'addTask')} />
                    <RecordButton name='addTask' recordingField={recordingField} setRecordingField={setRecordingField} settings={settings} handleKeyDown={handleKeyDown} theme={theme} />
                </div>
            </div>
            <div className="field-block mt-[30px]">
                <label className="field-label" style={{ color: theme == "dark" ? "white" : "", opacity: theme == "dark" ? 0.7 : '' }}>Search tasks</label>
                <div className="field-data">
                    <ShortcutKeyCombination name='searchTask' pressedKeys={shortcut?.searchTask} recordingField={recordingField} settings={settings} onReset={(e) => resetShortcut(e, 'searchTask')} onSetDefault={(e) => setDefaultShortcut(e, 'searchTask')} />
                    <RecordButton name='searchTask' recordingField={recordingField} setRecordingField={setRecordingField} settings={settings} handleKeyDown={handleKeyDown} theme={theme} />
                </div>
            </div>
            <div className="field-block mt-[30px]">
                <label className="field-label" style={{ color: theme == "dark" ? "white" : "", opacity: theme == "dark" ? 0.7 : '' }}>Open AI Assistant</label>
                <div className="field-data">
                    <ShortcutKeyCombination name='openAssistant' pressedKeys={shortcut?.openAssistant} recordingField={recordingField} settings={settings} onReset={(e) => resetShortcut(e, 'openAssistant')} onSetDefault={(e) => setDefaultShortcut(e, 'openAssistant')} />
                    <RecordButton name='openAssistant' recordingField={recordingField} setRecordingField={setRecordingField} settings={settings} handleKeyDown={handleKeyDown} theme={theme} />
                </div>
            </div>
        </div>
    )
}

export default KeyboardShortcuts